/**
 * WorkspaceAiSheet.js
 *
 * "Create with AI" sheet: the user describes the dashboard in a sentence, the
 * backend (`POST /api/dashboardWidgetAi/generate`, `target: 'mobile'`) picks a
 * compact set of widgets FROM the catalogue the phone offers (the allow-list is
 * the user's own accessible widgets, so the AI can never place a locked or
 * unknown one), and the result is PREVIEWED before anything is saved.
 *
 * @module components/workspaces/WorkspaceAiSheet
 */

import React, {useCallback, useEffect, useState} from 'react';
import {Text, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import PremiumSheet, {SheetChip, SheetInput, SheetLabel} from '../management/PremiumSheet';
import {useManagementTheme} from '../../helpers/managementTheme';
import haptics from '../../helpers/haptics';
import {WORKSPACE_MODULES, getWorkspaceWidget} from '../../config/workspaceCatalog';
import {generateWorkspaceLayout} from '../../services/mainDashboardApi';
import {SheetRow, useWorkspaceLabels} from './WorkspaceUI';

const EXAMPLES = ['wsd_ai_example_1', 'wsd_ai_example_2', 'wsd_ai_example_3'];

/**
 * @param {object} props
 * @param {boolean} props.visible Whether the sheet is shown.
 * @param {() => void} props.onClose Dismiss.
 * @param {Array<object>} props.availableWidgets Allow-list sent to the AI (`{id,title,description,category,kind}`).
 * @param {?string} props.restaurantId Store whose AI credit is metered.
 * @param {boolean} props.canReplace True when the open workspace is a widget board that can be replaced.
 * @param {boolean} props.canCreate False once the workspace cap is reached.
 * @param {(ids: string[]) => void} props.onCreate Create a new workspace from the previewed ids.
 * @param {(ids: string[]) => void} props.onReplace Replace the open workspace with the previewed ids.
 * @returns {JSX.Element} The sheet.
 */
export default function WorkspaceAiSheet({visible, onClose, availableWidgets, restaurantId, canReplace, canCreate, onCreate, onReplace}) {
    const {t, i18n} = useTranslation();
    const m = useManagementTheme();
    const labels = useWorkspaceLabels();
    const [prompt, setPrompt] = useState('');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (visible) {
            setResult(null);
            setError('');
            setBusy(false);
        }
    }, [visible]);

    const generate = useCallback(async () => {
        const text = prompt.trim();

        if (!text || busy) {
            return;
        }

        setBusy(true);
        setError('');
        setResult(null);

        try {
            const out = await generateWorkspaceLayout({prompt: text.slice(0, 500), availableWidgets, locale: i18n.language, restaurantId});
            const ids = Array.isArray(out?.widgetIds) ? out.widgetIds : [];

            if (!ids.length) {
                setError(t('wsd_ai_no_match'));
                haptics.warning();
            } else {
                setResult({ids, rationale: out.rationale || ''});
                haptics.success();
            }
        } catch (err) {
            setError(err?.status === 403 ? t('wsd_ai_credit') : t('wsd_ai_failed'));
            haptics.error();
        } finally {
            setBusy(false);
        }
    }, [prompt, busy, availableWidgets, i18n.language, restaurantId, t]);

    const ids = result?.ids || [];

    return (
        <PremiumSheet
            visible={visible}
            onClose={onClose}
            title={t('wsd_ai_title')}
            subtitle={t('wsd_ai_subtitle')}
            icon="creation"
            tone="violet"
            busy={busy}
            confirmLabel={result ? (canCreate ? t('wsd_ai_create') : t('wsd_ai_replace')) : t('wsd_ai_generate')}
            confirmIcon={result ? 'check' : 'creation'}
            confirmDisabled={result ? (!canCreate && !canReplace) : !prompt.trim()}
            confirmLoading={busy}
            onConfirm={() => {
                if (!result) {
                    generate();
                } else if (canCreate) {
                    onCreate(ids);
                } else {
                    onReplace(ids);
                }
            }}
        >
            <SheetLabel>{t('wsd_ai_prompt_label')}</SheetLabel>
            <SheetInput
                value={prompt}
                onChangeText={setPrompt}
                placeholder={t('wsd_ai_placeholder')}
                multiline
                maxLength={500}
                style={{minHeight: 84, textAlignVertical: 'top'}}
            />
            <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10}}>
                {EXAMPLES.map((key) => (
                    <SheetChip key={key} label={t(key)} maxLines={2} onPress={() => setPrompt(t(key))} />
                ))}
            </View>

            {!!error && <Text style={{color: m.colors.danger, marginTop: 12, fontSize: 13}}>{error}</Text>}

            {result && (
                <View style={{marginTop: 16}}>
                    <SheetLabel>{t('wsd_ai_preview')}</SheetLabel>
                    {!!result.rationale && <Text style={{color: m.colors.subText, fontSize: 13, marginBottom: 10}}>{result.rationale}</Text>}
                    {ids.map((id) => {
                        const definition = getWorkspaceWidget(id);
                        const visual = WORKSPACE_MODULES[definition?.module] || {icon: 'view-dashboard-outline', tone: 'primary'};

                        return <SheetRow key={id} icon={visual.icon} tone={visual.tone} title={labels.title(id)} onPress={() => {}} />;
                    })}
                    {canCreate && canReplace && (
                        <SheetChip label={t('wsd_ai_replace')} icon="swap-horizontal" onPress={() => onReplace(ids)} />
                    )}
                </View>
            )}
        </PremiumSheet>
    );
}
